<script lang="ts">
  import type { LayoutPreference } from '$types/races';

  import { createEventDispatcher } from 'svelte';
  import EditableTextOption from './EditableTextOption.svelte';

  import { page } from '$app/stores';
  import TablerLock from '~icons/tabler/lock';
  import TablerLockOpen from '~icons/tabler/lock-open';
  import TablerTrash from '~icons/tabler/trash';

  $: ({ layoutConfigStore } = $page.data);
  $: activeLayoutConfig = layoutConfigStore?.activeLayoutConfig;

  const dispatch = createEventDispatcher();
  export let preference: LayoutPreference;

  function deletePreference() {
    dispatch('delete', preference);
  }

  function updatePreference() {
    dispatch('update', preference);
  }

  $: preferenceTypeClass = () => {
    if (preference.bookmaker === 'Betfair') {
      return 'bg-orange-100 ';
    }
    switch (preference.type) {
      case 'FIXED_WIN':
        return 'bg-green-100 text-green-800';
      case 'FIXED_PLACE':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  $: preferenceTypeText = () => {
    switch (preference.type) {
      case 'FIXED_WIN':
        return 'Win';
      case 'FIXED_PLACE':
        return 'Place';
      case 'FIXED_WIN_FLUCS':
        return 'Win Flucs';
      case 'FIXED_PLACE_FLUCS':
        return 'Place Flucs';
      case 'TEXT':
        return `Text (${preference.size?.[0]})`;
      case 'BEST_PRICE_WIN':
        return 'Fixed Win';
      case 'BEST_PRICE_PLACE':
        return 'Fixed Place';
      case 'SPACER':
        return 'Spacer';
      default:
        return 'Show';
    }
  };

  $: betfairTypeText = () => {
    if (preference.bookmaker == 'Betfair') {
      let text = '';
      switch (preference.side) {
        case 'BACK':
          text += ' - Back';
          break;
        case 'LAY':
          text += ' - Lay';
          break;
        default:
          text += '';
          break;
      }
      switch (preference.level) {
        case 'ZERO':
          text += ' 0';
          break;
        case 'ONE':
          text += ' 1';
          break;
        case 'TWO':
          text += ' 2';
          break;
        case 'WEIGHTED_AVERAGE':
          text += ' WA';
          break;
        default:
          text += '';
      }
      return text;
    }
    return '';
  };

  function onOptionNameChange() {
    // firstly check if the new layout name exists already
    // or if it is empty
    // and if so, create a new name
    let layoutNameExists =
      ($activeLayoutConfig?.layoutPreferences.filter(
        (p) => p.name === preference.name
      ).length ?? 0) > 1
        ? true
        : false;
    if (!preference.name || layoutNameExists) {
      let layoutName = 'Custom';
      let layoutNumber = 1;
      layoutNameExists = true;
      while (layoutNameExists) {
        layoutNameExists = $activeLayoutConfig?.layoutPreferences.find(
          (p) => p.name === `${layoutName} ${layoutNumber}`
        )
          ? true
          : false;
        if (layoutNameExists) {
          layoutNumber++;
        } else {
          layoutName = `${layoutName} ${layoutNumber}`;
          break;
        }
      }
      preference.name = layoutName;
    }
    // strip whitespace from preference.name
    preference.name = preference.name.trim();

    updatePreference();

    // shouldSaveLayoutConfig.set(true);
  }
</script>

<div class="flex items-center space-x-4">
  <div>
    {#if preference.locked}
      <button
        class="cursor-pointer rounded-md p-2 text-red-600 hover:bg-slate-200 hover:text-green-600"
        on:click={() => {
          preference.locked = false;
          updatePreference();
        }}
        on:keypress={() => {
          preference.locked = false;
          updatePreference();
        }}
      >
        <TablerLock />
      </button>
    {:else}
      <button
        class="cursor-pointer rounded-md p-2 text-slate-600 hover:bg-slate-200 hover:text-red-500"
        on:click={() => {
          preference.locked = true;
          updatePreference();
        }}
        on:keypress={() => {
          preference.locked = true;
          updatePreference();
        }}
      >
        <TablerLockOpen />
      </button>
    {/if}
  </div>

  <div class="flex min-w-0 flex-1 flex-row">
    {#if preference.type === 'TEXT'}
      <EditableTextOption
        bind:value={preference.name}
        on:submit={onOptionNameChange}
      />
    {:else if preference.type === 'BEST_PRICE_WIN' || preference.type === 'BEST_PRICE_PLACE'}
      <div class="bookmaker-any-bookmaker mr-2 h-6 w-6 rounded-full" />
      <span class="text-md font-medium">Best Price</span>
    {:else}
      {#if preference.type !== 'SPACER'}
        <div
          class="mr-2 h-6 w-6 rounded-full bookmaker-{(
            preference?.bookmaker ?? ''
          )
            .toLowerCase()
            .replace(' ', '')}"
        />
      {/if}
      <span class="text-md font-medium"
        >{preference?.bookmaker ?? preference?.name ?? ''}</span
      >
    {/if}
  </div>
  <span
    class="mr-2 rounded px-2.5 py-0.5 text-sm font-medium {preferenceTypeClass()}"
    >{preferenceTypeText()}{betfairTypeText()}</span
  >
  <button
    class="cursor-pointer text-red-300 hover:text-red-500"
    on:click={deletePreference}
    on:keypress={deletePreference}
  >
    <TablerTrash />
  </button>
</div>
